import { useState } from 'react';
import { useCalendarStore } from '@/store/calendarStore';
import { formatDate, isSameDayAs, formatTime, formatDuration, getDaysInWeek, getWeekNumber, parseDateFromBackend } from '@/utils/dateUtils';
import { getDualDate, formatHebrewDate, getHebrewHolidays } from '@/utils/hebrewCalendar';
import { expandirReferencia } from '@/utils/bibliaHelper';
import { showExportDialog } from '@/utils/exportUtils';
import ItemPopup from './ItemPopup';
import { FiBook, FiCalendar, FiStar, FiClock, FiDownload } from 'react-icons/fi';

/**
 * Vista semanal del calendario
 * Muestra 7 días (domingo a sábado) con todos sus eventos
 */
export default function WeekView() {
  const { currentDate, citas, lecturas, eventos, settings, filters, setCurrentDate } = useCalendarStore();
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);

  console.log('📅 [WeekView] ========== RENDERIZANDO WeekView ==========');
  console.log('📅 [WeekView] Fecha actual:', currentDate);
  console.log('📅 [WeekView] 📚 Total lecturas en store:', lecturas?.length || 0);
  console.log('📅 [WeekView] 🔍 Filtros activos:', filters);

  // Obtener los 7 días de la semana actual
  const weekDays = getDaysInWeek(currentDate);
  const weekNumber = getWeekNumber(currentDate);
  const today = new Date();

  // Función para obtener items de un día específico
  const getItemsForDay = (date) => {
    const dayCitas = filters.showCitas ? (citas || []).filter(cita => {
      try {
        const citaDate = parseDateFromBackend(cita.fechaInicio);
        return isSameDayAs(citaDate, date);
      } catch {
        return false;
      }
    }) : [];

    const dayLecturas = filters.showLecturas ? (lecturas || []).filter(lectura => {
      try {
        const lecturaDate = parseDateFromBackend(lectura.fecha);
        const isSameDay = isSameDayAs(lecturaDate, date);
        
        if (isSameDay && filters.tiposLectura.length > 0) {
          return filters.tiposLectura.includes(lectura.tipo);
        }
        
        return isSameDay;
      } catch {
        return false;
      }
    }) : [];

    const dayEventos = filters.showEventos ? (eventos || []).filter(evento => {
      try {
        const eventoDate = parseDateFromBackend(evento.fechaInicio);
        return isSameDayAs(eventoDate, date);
      } catch {
        return false;
      }
    }) : [];

    return { 
      citas: dayCitas, 
      lecturas: dayLecturas, 
      eventos: dayEventos,
      total: dayCitas.length + dayLecturas.length + dayEventos.length
    };
  };

  const handleItemClick = (e, item, type) => {
    e.stopPropagation();
    setSelectedItem(item);
    setSelectedItemType(type);
  };

  const closePopup = () => {
    setSelectedItem(null);
    setSelectedItemType(null);
  };

  // Función para verificar si una fecha está dentro de una cita multi-día
  const getActiveCitaForDay = (day) => {
    if (!filters.showCitas) return null;
    
    return citas.find(cita => {
      try {
        const inicio = parseDateFromBackend(cita.fechaInicio);
        const fin = parseDateFromBackend(cita.fechaFin);
        
        // Normalizar fechas a medianoche para comparación
        const diaActual = new Date(day.getFullYear(), day.getMonth(), day.getDate());
        const diaInicio = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
        const diaFin = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
        
        // Verificar si es una cita multi-día
        const esMultiDia = diaInicio.getTime() !== diaFin.getTime();
        
        // Verificar si el día actual está dentro del rango
        const estaDentroDelRango = diaActual >= diaInicio && diaActual <= diaFin;
        
        return esMultiDia && estaDentroDelRango;
      } catch {
        return false;
      }
    });
  };

  const isToday = (date) => isSameDayAs(date, today);
  const isCurrentDate = (date) => isSameDayAs(date, currentDate);

  // Calcular total de eventos de la semana
  const weekTotalCitas = weekDays.reduce((sum, day) => sum + getItemsForDay(day).citas.length, 0);
  const weekTotalLecturas = weekDays.reduce((sum, day) => sum + getItemsForDay(day).lecturas.length, 0);
  const weekTotalEventos = weekDays.reduce((sum, day) => sum + getItemsForDay(day).eventos.length, 0);
  const weekTotalItems = weekTotalCitas + weekTotalLecturas + weekTotalEventos;

  // Función para exportar
  const handleExport = () => {
    const daysData = weekDays.map(day => ({
      date: day,
      items: {
        citas: getItemsForDay(day).citas,
        lecturas: getItemsForDay(day).lecturas,
        eventos: getItemsForDay(day).eventos
      }
    }));
    
    const title = `Calendario - Vista Semana ${weekNumber} - ${formatDate(weekDays[0], 'd MMM')} a ${formatDate(weekDays[6], 'd MMM yyyy')}`;
    showExportDialog(daysData, 'week', title);
  };

  return (
    <>
    <div className="bg-white shadow-md rounded-lg p-4 relative">
      {/* Botón de exportar */}
      <button
        onClick={handleExport}
        className="absolute top-4 right-4 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-md z-10"
        title="Exportar semana"
      >
        <FiDownload className="w-4 h-4" />
        <span className="text-sm font-medium">Exportar</span>
      </button>

      {/* Encabezado de la semana */}
      <div className="mb-4 pb-3 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Semana {weekNumber} de {currentDate.getFullYear()}
            </h3>
            <p className="text-sm text-gray-600">
              {formatDate(weekDays[0], 'd MMM')} - {formatDate(weekDays[6], 'd MMM yyyy')}
            </p>
          </div>
          {settings?.showDualCalendar && (
            <div className="text-right">
              <p className="text-sm text-gray-600">
                {formatHebrewDate(getDualDate(currentDate, settings?.hebrewDayStart || '18:00').hebrew)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Grid de días de la semana */}
      <div className="grid grid-cols-7 gap-2 pb-20">
        {weekDays.map((date, index) => {
          const items = getItemsForDay(date);
          const totalItems = items.citas.length + items.lecturas.length + items.eventos.length;
          const holidays = filters.showFestividadesJudias ? getHebrewHolidays(date) : [];
          const dayIsToday = isToday(date);
          const dayIsSelected = isCurrentDate(date);
          const activeCita = getActiveCitaForDay(date);

          return (
            <div
              key={index}
              onClick={() => setCurrentDate(date)}
              className={`
                border rounded-lg p-2 min-h-[120px] cursor-pointer transition-all relative overflow-hidden flex flex-col
                ${dayIsToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                ${dayIsSelected ? 'ring-2 ring-blue-400' : ''}
                hover:shadow-md hover:border-blue-300
              `}
            >
              {/* Sombreado de hora hebrea (6 PM en adelante) - lado derecho */}
              <div 
                className="absolute top-0 right-0 bottom-0 w-1/3 bg-gray-500 opacity-30 pointer-events-none z-0"
                title="Inicio del día hebreo (6:00 PM)"
              ></div>

              {/* Sombreado para citas ordenadas multi-día */}
              {activeCita && (
                <div 
                  className="absolute inset-0 pointer-events-none z-[1]"
                  style={{ 
                    backgroundColor: activeCita.color,
                    opacity: 0.12
                  }}
                  title={`${activeCita.nombre} (en progreso)`}
                ></div>
              )}

              <div className="relative z-10 flex-1 flex flex-col">
                {/* Encabezado del día */}
                <div className="mb-2 pb-1 border-b border-gray-200">
                  <p className={`text-xs font-semibold ${dayIsToday ? 'text-blue-600' : 'text-gray-500'}`}>
                    {formatDate(date, 'EEE')}
                  </p>
                  <p className={`text-lg font-bold ${dayIsToday ? 'text-blue-600' : 'text-gray-800'}`}>
                    {date.getDate()}
                  </p>
                </div>

              {/* Festividades hebreas */}
              {holidays.length > 0 && (
                <div className="mb-2">
                  {holidays.map((holiday, idx) => (
                    <p key={idx} className="text-xs text-purple-600 font-medium truncate" title={holiday}>
                      🕎 {holiday}
                    </p>
                  ))}
                </div>
              )}

              {/* Items del día - con flex-1 para ocupar espacio disponible y overflow-auto para scroll */}
              <div className="space-y-1 flex-1 min-h-0 overflow-auto">
                {/* Citas - Prioridad 1 - Mostrar hasta 20 items (más que suficiente) */}
                {items.citas.slice(0, 20).map((cita, idx) => (
                  <div
                    key={`cita-${idx}`}
                    onClick={(e) => handleItemClick(e, cita, 'cita')}
                    className="text-xs p-1 rounded flex items-start gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: `${cita.color}20`, borderLeft: `3px solid ${cita.color}` }}
                  >
                    <FiCalendar className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: cita.color }} />
                    <span className="truncate flex-1" title={cita.nombre}>
                      {cita.nombre}
                    </span>
                  </div>
                ))}

                {/* Lecturas - Prioridad 2 - Mostrar hasta 20 items */}
                {items.lecturas.slice(0, 20).map((lectura, idx) => (
                  <div
                    key={`lectura-${idx}`}
                    onClick={(e) => handleItemClick(e, lectura, 'lectura')}
                    className="text-xs p-1 rounded flex items-start gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: `${lectura.color}20`, borderLeft: `3px solid ${lectura.color}` }}
                  >
                    <FiBook className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: lectura.color }} />
                    <span className="truncate flex-1" title={`${lectura.tipo}: ${expandirReferencia(lectura.referencia)}`}>
                      {lectura.tipo}
                    </span>
                  </div>
                ))}

                {/* Eventos - Prioridad 3 - Mostrar hasta 20 items */}
                {items.eventos.slice(0, 20).map((evento, idx) => (
                  <div
                    key={`evento-${idx}`}
                    onClick={(e) => handleItemClick(e, evento, 'evento')}
                    className="text-xs p-1 rounded flex items-start gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: `${evento.color}20`, borderLeft: `3px solid ${evento.color}` }}
                  >
                    <FiStar className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: evento.color }} />
                    <span className="truncate flex-1" title={evento.nombre}>
                      {evento.nombre}
                    </span>
                  </div>
                ))}

                {/* Indicador de más items - solo si hay más de 20 de algún tipo */}
                {(items.citas.length > 20 || items.lecturas.length > 20 || items.eventos.length > 20) && (
                  <p className="text-xs text-gray-500 text-center py-0.5">
                    ...
                  </p>
                )}

                {/* Mensaje si no hay items */}
                {totalItems === 0 && !holidays.length && (
                  <p className="text-xs text-gray-400 text-center pt-2">
                    Sin eventos
                  </p>
                )}
              </div>

              {/* Contador en esquina inferior izquierda - en su propio espacio */}
              {totalItems > 0 && (
                <div className="mt-auto pt-1">
                  <div className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold shadow-sm bg-gray-700 text-white inline-block">
                    {totalItems}
                  </div>
                </div>
              )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-50 border-2 border-blue-500 rounded"></div>
            <span>Hoy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-blue-400 rounded ring-2 ring-blue-400"></div>
            <span>Día seleccionado</span>
          </div>
          {filters.showCitas && (
            <div className="flex items-center gap-2">
              <FiCalendar className="w-3 h-3" />
              <span>Citas</span>
            </div>
          )}
          {filters.showLecturas && (
            <div className="flex items-center gap-2">
              <FiBook className="w-3 h-3" />
              <span>Lecturas</span>
            </div>
          )}
          {filters.showEventos && (
            <div className="flex items-center gap-2">
              <FiStar className="w-3 h-3" />
              <span>Eventos</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-500 opacity-40 border-r-4 border-gray-500"></div>
            <span>Día hebreo (6 PM) →</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500 opacity-20"></div>
            <span>Cita multi-día activa</span>
          </div>
        </div>
      </div>

      {/* Contador de eventos totales de la semana en esquina inferior izquierda */}
      {weekTotalItems > 0 && (
        <div className="absolute bottom-4 left-4 z-10">
          <div className="bg-gray-700 text-white px-3 py-2 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">Total semana:</span>
              <span className="text-sm font-bold">{weekTotalItems}</span>
            </div>
            <div className="text-[10px] text-gray-300 mt-1">
              Citas: {weekTotalCitas} | Lecturas: {weekTotalLecturas} | Eventos: {weekTotalEventos}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Popup de detalles */}
    {selectedItem && (
      <ItemPopup 
        item={selectedItem}
        type={selectedItemType}
        onClose={closePopup}
      />
    )}
    </>
  );
}

